import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import MetaTags from "react-meta-tags";
import SweetAlert from "react-bootstrap-sweetalert";
import { Row, Card, CardBody, Col, Container, FormGroup, Label } from "reactstrap";
import { AvField, AvRadioGroup, AvRadio } from "availity-reactstrap-validation";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import { motion, AnimatePresence } from "framer-motion";
import { Modal, ModalHeader, ModalBody } from "reactstrap";

import { priorityPLApi } from "../../services/PriorityPLService";
import {
  Newspaper,
  BadgePlus,
  ChevronDown,
  Package,
  CheckCircle,
  AlertCircle,
  BicepsFlexed,
  FileBox,
  Rows2,
  MessageCircleReply,
  AlarmClock,
  HandCoins,
  PackageMinus,
  ScrollText,
  BadgeInfo,
  Copy,
  Snail,
  Zap,
  UserPen,
  ReceiptText,
  ShoppingCart,
  FlaskConicalOff,
  Turtle,
  Rabbit,
  Bird,
  Snowflake,
  Rocket,
  Euro,
  Banknote,
  AlertTriangle,
} from "lucide-react";
import { InfoPopover } from "./InfoPopover";
import { fetchTrackingStatuses } from "../../api/trackingApi";
import TrackingColumnPrTest from "../../components/TrackingColumnPrTest";
import Breadcrumbs from "../../components/Breadcrumb";
import PageLoader from "../../components/Loaders/newPageLoader";
import { showToast } from "../../components/Toast";
import useLoadingProgress from "../../hooks/useLoadingProgress";
import { MainContent } from "../../components/Table/MainContent";
import { BtnModal } from "../../components/Table/BtnModal";
import InputMask from "react-input-mask";
import cellEditFactory, { Type } from "react-bootstrap-table2-editor";
import Select, { components } from "react-select";

export default function PriorityPL({ profileData, loading }) {
  console.count("PriorityPL render");
  const {
    data = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = priorityPLApi.useFetchAllPriorityPLQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
  });

  const [showLogModal, setShowLogModal] = useState(false);
  const [logModalOrderId, setLogModalOrderId] = useState(null);

  const {
    data: logs = [],
    isLoading: logsLoading,
    isFetching: logsFetching,
    refetch: refetchLogs,
  } = priorityPLApi.useGetLogsByOrderIdQuery(logModalOrderId, {
    skip: !showLogModal || !logModalOrderId,
    refetchOnMountOrArgChange: true,
  });

  const [addPriorityPL] = priorityPLApi.useAddPriorityPLMutation();
  const [updatePriorityPL] = priorityPLApi.useUpdatePriorityPLMutation();
  const [deletePriorityPL] = priorityPLApi.useDeletePriorityPLMutation();

  const [priorityPL, setPriorityPL] = useState([]);
  const [slManagers, setSlManagers] = useState([]);

  const [filters, setNoDbFilters] = useState([]);
  const [order, setSelectedOrder] = useState([]);
  const [profile, setSelectedProfile] = useState([]);

  //   const [access_user, setAccessSelectedUser] = useState([]);
  //   const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  //   const [departments, setDepartmentsData] = useState([]);
  //   const [accesses, setAccessesData] = useState([]);

  // const { progress, step, visible } = useLoadingProgress({
  //   isLoading,
  //   isFetching,
  // });
  useEffect(() => {
    if (data.priorityPL && data.SlManagers) {
      setPriorityPL(JSON.parse(JSON.stringify(data.priorityPL)));
      setSlManagers(JSON.parse(JSON.stringify(data.SlManagers)));
    }
  }, [data]);

  // useEffect(() => {
  //   if (showLogModal && logModalOrderId) {
  //     refetchLogs(); // ✅ Fetch fresh logs every time modal opens with an ID
  //   }
  // }, [showLogModal, logModalOrderId]);

  const [cardHeight, setCardHeight] = useState(0);
  const cardRef = useRef(null);

  const [selectedRows, setSelectedRows] = useState([]);

  useEffect(() => {
    if (!cardRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const newHeight = entry.contentRect.height;
        if (newHeight > 0) setCardHeight(newHeight);
      }
    });
    resizeObserver.observe(cardRef.current);
    return () => resizeObserver.disconnect();
  }, []);
  const filteredPriorityPL = useMemo(() => {
    let result = [...priorityPL];

    if (filters?.status?.length > 0) {
      result = result.filter((order) => filters.status.includes(order.status));
    } else {
      result = result.filter((row) => row.status !== "sent");
    }

    const isWH = profileData?.department?._id === "69b12949fad30e48f0dd2667";

    if (isWH) {
      result = result.filter((row) => row.sendDate || row.status === "only_pack");
      result = result.filter((row) => row.sendType);
    }

    return result;
  }, [filters, priorityPL, profileData?.department?._id]);

  // Toggle modal
  const defaultDateStr = () => new Date().toLocaleDateString("en-GB");

  const [modalNewOrder, setModalNewOrder] = useState(false);

  const defaultDate = useMemo(defaultDateStr, []);
  const [placedDate, setPlacedDate] = useState(defaultDate);
  const [sendDate, setSendDate] = useState(defaultDate);

  const [correctSendDate, setCorrectSendDate] = useState(true);

  const onChangeDate = (event) => {
    const { name, value } = event.target;
    // month is characters at positions 3 and 4 in dd/mm/yyyy
    const monthStr = value.substring(3, 5);
    const month = parseFloat(monthStr);

    const isInvalid = month > 12;

    if (name === "sendDate") {
      if (isInvalid) {
        setCorrectSendDate(false);
      } else {
        setCorrectSendDate(true);
        setSendDate(value);
      }
    }
  };

  /**
   * Toggles the visibility state of the "New Order" modal.
   * Uses a callback to invert the current modal state.
   *
   * @function
   * @returns {void}
   */
  const toggleNewOrderModal = useCallback(() => {
    setModalNewOrder((prev) => !prev);
  }, []);

  /**
   * Handles the event when the "New Order" button is clicked.
   * Resets the selected order and access selections, sets edit mode to false,
   * and opens the modal for creating a new order.
   */
  const handleNewOrderClick = () => {
    setSelectedOrder([]);
    setIsEdit(false);
    toggleNewOrderModal();
  };
  /**
   * Handles the event when a order is selected for editing.
   * Sets the selected order's details in the state, resets the selected access,
   * enables edit mode, and opens the order modal for editing.
   *
   * @param {Object} order - The order object containing order details to be edited.
   */
  const handleOrderClickEdit = (order) => {
    setSelectedOrder({
      _id: order._id,
      orderNumber: order.orderNumber,
      clientName: order.clientName,
      placedDate: order.placedDate,
      sendDate: order.sendDate,
      boxQty: order.boxQty,
      boxSize: order.boxSize,
      address: order.address,
      trackingNo: order.trackingNo,
      comments: order.comments,
      sendType: order.sendType,
      manager: order.manager,
      status: order.status,
    });

    setIsEdit(true);
    toggleNewOrderModal();
  };

  const ddmmyyyyToZulu = (ddmmyyyy) => {
    const [dd, mm, yyyy] = ddmmyyyy.split("/");
    return `${yyyy}-${mm}-${dd}T00:00:00.000Z`;
  };

  /**
   * Handles the submission of the new order or edit order form.
   * Depending on the mode (add or edit), it constructs the appropriate order object
   * and performs the corresponding API call to add or update a order in the system.
   * Displays toast notifications based on the outcome and toggles the order modal on success.
   *
   * @async
   * @function
   * @param {Event} e - The form submission event.
   * @param {Object} values - The form values containing order details.
   * @returns {Promise<void>}
   */
  const handleNewOrderSubmit = async (e, values) => {
    const selectedManager = slManagers.find((m) => m._id === values.manager);

    if (isEdit) {
      const updOrder = {
        _id: order._id,
        orderNumber: values.orderNumber,
        clientName: values.clientName,
        placedDate: ddmmyyyyToZulu(placedDate),
        sendDate: ddmmyyyyToZulu(sendDate),
        boxQty: values.boxQty,
        boxSize: values.boxSize,
        address: values.address,
        trackingNo: values.trackingNo,
        comments: values.comments,
        sendType: values.sendType,
        manager: values.manager,
        bitrixID: selectedManager.bitrixID,
        status: values.status,
      };
      try {
        await updatePriorityPL(updOrder).unwrap();
        toggleNewOrderModal();
        showToast("Order is Updated");
      } catch (error) {
        showToast("Failed to Update Order information", "error");
      }
    } else {
      const newOrder = {
        orderNumber: values["orderNumber"],
        clientName: values["clientName"],
        placedDate: placedDate,
        sendDate: ddmmyyyyToZulu(sendDate),
        boxQty: values["boxQty"],
        boxSize: values["boxSize"],
        address: values["address"],
        trackingNo: values["trackingNo"],
        comments: values["comments"],
        sendType: values["sendType"],
        koruMSID: values["manager"],
        bitrixID: selectedManager.bitrixID,
        status: "",
      };
      try {
        await addPriorityPL(newOrder).unwrap();
        toggleNewOrderModal();
        showToast("New Order is Added");
      } catch (error) {
        showToast("Failed to Add New Order", "error");
      }
    }
  };

  const [rowToDelete, setRowToDelete] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  /**
   * Handles the delete action when a order is clicked for deletion.
   * Sets the selected order, marks the order's ID for deletion, and displays the delete confirmation alert.
   *
   * @param {Object} order - The order object representing the order to be deleted.
   */
  const handleOrderClickDelete = (order) => {
    setSelectedOrder(order);
    setRowToDelete(order._id);
    setShowDeleteAlert(true);
  };

  const handleBulkAction = async (selectedIds, actionValue) => {
    if (!selectedIds || selectedIds.length === 0) return;

    // if (actionValue === "delete") {
    //   await Promise.all(
    //     selectedIds.map((id) =>
    //       updatePriorityMoscow({ _id: id, deleted: true }),
    //     ),
    //   );
    // }

    if (actionValue.startsWith("status:")) {
      const statusValue = actionValue.split(":")[1];
      await Promise.all(selectedIds.map((id) => updatePriorityPL({ _id: id, status: statusValue })));
    }

    setSelectedRows([]);
    // setAction("");
  };

  /**
   * Handles the deletion of a order.
   *
   * This asynchronous function attempts to delete the currently selected order.
   * On successful deletion, it resets relevant UI states and displays a success toast notification.
   * If the deletion fails, it logs the error and shows an error toast notification.
   * The function also ensures that any loading or deleting state can be reset after the operation.
   *
   * @async
   * @function handleDeleteOrder
   * @returns {Promise<void>} Resolves when the order deletion process is complete.
   */
  const handleDeleteOrder = async () => {
    try {
      //   setIsDeleting(true);
      await deletePriorityPL(rowToDelete);
      setRowToDelete(null);
      setShowDeleteAlert(false);
      setSelectedOrder([]);
      showToast("Order deleted successfully");
    } catch (error) {
      showToast("Failed to delete row", "error");
    } finally {
      // setIsDeleting(false); // Reset loading state after completion
    }
  };

  const handleSaveCell = async (oldValue, newValue, row, column) => {
    if (oldValue === newValue) return;

    const field = column.dataField;

    // Нормализация Delivery Fee
    if (field === "deliveryFee") {
      const normalized = String(newValue ?? "")
        .trim()
        .replace(",", ".");

      if (normalized === "") {
        newValue = null;
      } else {
        const numericValue = Number(normalized);

        if (!Number.isFinite(numericValue) || numericValue < 0) {
          showToast("Enter a valid Delivery Fee", "error");
          return;
        }

        newValue = numericValue;
      }
    }

    // --- 1) Сбор апдейта для сервера (безопасно к manager === null) ---
    const updateOrder = {
      _id: row._id,
      orderNumber: row.orderNumber,
      clientName: row.clientName,
      managerID: profileData?._id,
      ...(row.manager?.bitrixID ? { bitrixID: row.manager.bitrixID } : {}),
      [field]:
        field === "sendDate" && newValue
          ? new Date(newValue).toISOString() // нормализуем дату
          : newValue,
    };

    if (column.dataField === "trackingNo") {
      const trackings =
        typeof newValue === "string"
          ? newValue
              .split(/[, .\n]+/)
              .map((num) => num.trim())
              .filter((num) => /\d/.test(num))
          : Array.isArray(newValue)
            ? newValue
            : [];

      updateOrder.trackingNo = trackings;

      // Автоматически определяем Currency
      const firstTracking = trackings[0] || "";

      updateOrder.currency = !firstTracking ? "" : firstTracking.toUpperCase().startsWith("1Z") ? "PNL" : "EUR";
    }

    setPriorityPL((prevTableData) =>
      prevTableData.map((item) =>
        item._id === row._id
          ? {
              ...item,
              [column.dataField]: newValue,
              ...(column.dataField === "trackingNo" ? { currency: updateOrder.currency } : {}),
            }
          : item,
      ),
    );

    try {
      await updatePriorityPL(updateOrder).unwrap();

      if (column.dataField === "trackingNo" && updateOrder.trackingNo?.length > 0) {
        await fetchTrackingStatuses(updateOrder.trackingNo, "register");
      }
      if (logModalOrderId === row._id) {
        refetchLogs();
      }
      showToast("Update has been done");
    } catch (err) {
      // --- откат ---
      showToast("Failed to update row", "error");
    }
  };

  const STATUS_OPTIONS = [
    {
      key: "under_work",
      label: "Under Work",
      icon: <BicepsFlexed size={18} className="me-2" />,
      colorClass: "bg-primary",
    },
    {
      key: "request_to_pack",
      label: "Sent Request to Pack",
      icon: <FileBox size={18} className="me-2" />,
      colorClass: "bg-warning",
    },
    {
      key: "packaged",
      label: "Packaged",
      icon: <Package size={18} className="me-2" />,
      colorClass: "bg-info bg-gradient",
    },
    {
      key: "request_slip_docs",
      label: "Requested Slip/Docs",
      icon: <Package size={18} className="me-2" />,
      colorClass: "bg-info bg-gradient",
    },
    {
      key: "wait_pickup",
      label: "Waiting Pickup",
      icon: <AlarmClock size={18} className="me-2" />,
      colorClass: "pink-color",
    },
    {
      key: "prep_slip",
      label: "Preparing Slip",
      icon: <ReceiptText size={18} className="me-2" />,
      colorClass: "pink-color",
    },
    {
      key: "half_pack",
      label: "Half Pack",
      icon: <Rows2 size={18} className="me-2" />,
      colorClass: "pink-color",
    },
    {
      key: "sent",
      label: "Sent",
      icon: <CheckCircle size={18} className="me-2" />,
      colorClass: "bg-success bg-gradient",
    },

    {
      key: "stopped",
      label: "Stopped",
      icon: <AlertCircle size={18} className="me-2" />,
      colorClass: "bg-danger bg-gradient",
    },
    {
      key: "waiting_compensation_fee",
      label: "Waiting Compensation Fee",
      icon: <MessageCircleReply size={18} className="me-2" />,
      colorClass: "bg-dark bg-opacity-75",
    },
    {
      key: "only_pack",
      label: "ONLY PACK",
      icon: <PackageMinus size={18} className="me-2" />,
      colorClass: "bg-primary",
    },
    {
      key: "only_pack_under_work",
      label: "ONLY PACK - Under Work",
      icon: <BicepsFlexed size={18} className="me-2" />,
      colorClass: "bg-primary",
    },
    {
      key: "only_pack_req_to_pack",
      label: "ONLY PACK - Sent request to Pack",
      icon: <FileBox size={18} className="me-2" />,
      colorClass: "bg-primary",
    },
    {
      key: "only_pack_packaged",
      label: "ONLY PACK - Packaged",
      icon: <Package size={18} className="me-2" />,
      colorClass: "bg-primary",
    },
    {
      key: "only_pack_allow_send",
      label: "ONLY PACK - Allow to Send",
      icon: <HandCoins size={18} className="me-2" />,
      colorClass: "bg-success",
    },
  ];

  const STATUS_OPTIONS_SELECT = [
    { value: "under_work", text: "Under Work", className: "bg-primary" },
    {
      value: "request_to_pack",
      text: "Sent Request to Pack",
      className: "bg-warning",
    },
    { value: "packaged", text: "Packaged", className: "bg-info bg-gradient" },
    { value: "wait_pickup", text: "Waiting Pickup", className: "pink-color" },
    { value: "half_pack", text: "Half Pack", className: "pink-color" },
    { value: "sent", text: "Sent", className: "bg-success bg-gradient" },
    { value: "stopped", text: "Stopped", className: "bg-danger bg-gradient" },
    {
      value: "waiting_compensation_fee",
      text: "Waiting Compensation Fee",
      className: "bg-dark bg-opacity-75",
    },
    { value: "only_pack", text: "ONLY PACK", className: "bg-primary" },
    {
      value: "only_pack_under_work",
      text: "ONLY PACK - Under Work",
      className: "bg-primary",
    },
    {
      value: "only_pack_req_to_pack",
      text: "ONLY PACK - Sent request to Pack",
      className: "bg-primary",
    },
    {
      value: "only_pack_packaged",
      text: "ONLY PACK - Packaged",
      className: "bg-primary",
    },
    {
      value: "only_pack_allow_send",
      text: "ONLY PACK - Allow to Send",
      className: "bg-success",
    },
  ];

  const handleGlobalActionSubmit = (e, values) => {
    // const { action } = values;
    // const setPaidSentArr = selectedRows.map((id) => {
    //   let data = { _id: id };
    //   switch (action) {
    //     case "set-paid":
    //       data.agentPaid = true;
    //       break;
    //     case "set-unpaid":
    //       data.agentPaid = false;
    //       break;
    //     case "set-not-completed":
    //       data.completed = false;
    //       break;
    //     case "set-work-needed":
    //       data.workNeeded = true;
    //       break;
    //     case "update-status-working":
    //       data.currentlyWorking = true;
    //       break;
    //     case "update-status-completed":
    //       data.workDone = true;
    //       break;
    //     case "set-sent-from-agent":
    //       data.sentFromAgent = true;
    //       // data.problem = false;
    //       break;
    //     case "set-completed":
    //       data.completed = true;
    //       data.problem = false;
    //       break;
    //     case "set-problem":
    //       data.problem = true;
    //       data.completed = false;
    //       break;
    //     case "fully-reset":
    //       data = {
    //         _id: id,
    //         workNeeded: false,
    //         currentlyWorking: false,
    //         sentFromAgent: false,
    //         workDone: false,
    //         completed: false,
    //         problem: false,
    //       };
    //       break;
    //     default:
    //       break;
    //   }
    //   return data;
    // });
    // const id = isChecked ? profileData?._id : "all";
    // onUpdateTransfer({ dispatch, updatedData: { updateTransfer: setPaidSentArr, id: id } });
    // setSelectedRows([]);
    // setSelectedRowsData([]);
    // setSelectedWeights(0);
    // setSelectedAgentFees(0);
    // setSelectedNoOfToxin(0);
  };

  const getCurrencyByTracking = (trackingNo) => {
    const trackingNumbers = Array.isArray(trackingNo)
      ? trackingNo
      : typeof trackingNo === "string"
        ? trackingNo.split(/[,\n;]/)
        : trackingNo && typeof trackingNo === "object"
          ? Object.values(trackingNo).flat()
          : [];

    const firstTracking = trackingNumbers.map((value) => String(value || "").trim()).find(Boolean);

    if (!firstTracking) return "";

    return firstTracking.toUpperCase().startsWith("1Z") ? "PNL" : "EUR";
  };

  const getStatusMeta = (key) => STATUS_OPTIONS.find((status) => status.key === key);

  /**
   * Renders the content for the "New Order" modal form.
   *
   * This component displays a multi-row form for creating or editing a order, including fields for personal information,
   * department, supervisor, position, contact details, leave days, access rights, and role. It uses AvField components
   * for validation and supports dynamic population of select options for departments and supervisors.
   *
   * @constant
   * @type {JSX.Element}
   * @param {Object} order - The order object containing current form values.
   * @param {Array} departments - Array of department objects for the department select field.
   * @param {Array} orders - Array of order objects for the supervisor select field.
   * @param {Object} error - Error object for displaying validation errors.
   * @returns {JSX.Element} The rendered form content for the modal.
   */
  const modalNewOrderContent = (
    <>
      <Row>
        <Col md={3}>
          <FormGroup className="mb-3">
            <AvField
              name="orderNumber"
              label="Invoice No"
              type="text"
              errorMessage="Invalid Invoice No"
              validate={{
                required: { value: true },
              }}
              value={order.orderNumber || ""}
            />
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup className="mb-3">
            <AvField
              name="clientName"
              label="Client Name"
              type="text"
              errorMessage="Invalid Client Name"
              validate={{
                required: { value: true },
              }}
              value={order.clientName || ""}
            />
          </FormGroup>
        </Col>
        <Col md={2}>
          <FormGroup className="mb-3">
            <AvField
              name="placedDate"
              label="Date"
              mask="99/99/9999"
              value={placedDate}
              onChange={(e) => setPlacedDate(e.target.value)}
              placeholder="DD/MM/YYYY"
              className="form-control input-mask"
              readOnly
              validate={{
                required: {
                  value: true,
                  errorMessage: "Date is required",
                },
                pattern: {
                  value: /^(0[1-9]|[12]\d|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
                  errorMessage: "Use DD/MM/YYYY",
                },
              }}
            />
          </FormGroup>
        </Col>
        <Col md={2}>
          <FormGroup className="mb-3">
            <Label>Send Date</Label>
            <InputMask
              mask="99/99/9999"
              value={sendDate}
              name="sendDate"
              id="sendDate"
              onChange={onChangeDate}
              className={correctSendDate ? "form-control input-mask" : "form-control input-mask is-invalid"}
              placeholder="dd/mm/yyyy"
            />
            {!correctSendDate && <div className="invalid-feedback">Invalid Date. Please use dd/mm/yyyy format</div>}
          </FormGroup>
        </Col>
        <Col md={2}>
          <FormGroup className="mb-3">
            <AvField
              type="select"
              name="manager"
              className="form-select"
              label="Manager"
              errorMessage="Please select manager"
              validate={{
                required: {
                  value: true,
                },
              }}
              value={order.manager ? order.manager._id : profileData._id}
            >
              <option>Choose</option>
              {slManagers &&
                slManagers.map((manager) => {
                  return (
                    <option key={manager._id} value={manager._id}>
                      {manager.fname} {manager.sname}
                    </option>
                  );
                })}
            </AvField>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={3}>
          <FormGroup className="mb-3">
            <AvField name="trackingNo" label="Tracking" type="text" errorMessage="Invalid Tracking" value={order.trackingNo || ""} />
          </FormGroup>
        </Col>

        <Col md={3}>
          <FormGroup className="mb-3">
            <AvField type="select" name="sendType" className="form-select" label="Send Type" value={order.sendType || ""}>
              <option>Select Send Type</option>
              <option key="cheapSlow" value="cheapSlow">
                Cheap - Slow
              </option>
              <option key="ExpFast" value="ExpFast">
                Expesive - Fast
              </option>
            </AvField>
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup className="mb-3">
            <AvField name="boxQty" label="Box Qty" type="text" value={order.boxQty || ""} />
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup className="mb-3">
            <AvField name="boxSize" label="Box Size" type="text" value={order.boxSize || ""} />
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <FormGroup className="mb-3">
            <AvField name="address" label="Address" type="textarea" rows="5" errorMessage="Invalid address" value={order.address || ""} className="form-control" />
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup className="mb-3">
            <AvField name="comments" label="Comments" type="textarea" rows="5" errorMessage="Invalid Comment" value={order.comments || ""} className="form-control" />
          </FormGroup>
        </Col>
      </Row>
    </>
  );

  const columns = [
    {
      text: "No",
      dataField: "",
      sort: true,
      editable: false,
      csvExport: false,
      formatter: (cell, row, rowIndex) => rowIndex + 1,
      headerClasses: "id-header",
    },
    {
      text: "Client Name",
      dataField: "clientName",
      sort: true,
      editable: true,
      formatter: (cellContent, order) => {
        return <h5 className="text-break text-center">{order.clientName}</h5>;
      },
      style: {
        width: "15ch",
        maxWidth: "15ch",
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },
    {
      text: "Invoice No",
      dataField: "orderNumber",
      sort: true,
      formatter: (cellContent, order) => (
        <>
          <div className="d-flex justify-content-between align-items-center">
            <Copy
              size={20}
              color="#497ea7"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation(); // Prevent unintended actions

                const textarea = document.createElement("textarea");
                textarea.value = order.clientName + " " + order.orderNumber;
                textarea.style.position = "fixed"; // Avoid scrolling
                textarea.style.opacity = 0; // Make invisible
                document.body.appendChild(textarea);
                textarea.select();

                try {
                  if (document.execCommand("copy")) {
                    showToast("success", "Copied to clipboard"); // ✅ Show success only if copy works
                  } else {
                    showToast("error", "Failed to copy tracking numbers.");
                  }
                } catch (err) {
                  showToast("error", "Failed to copy tracking numbers.");
                }

                document.body.removeChild(textarea); // Clean up the textarea
              }}
            />
            <p className="mb-0">{order.orderNumber}</p>
            <Copy
              size={20}
              color="#ac0404"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation(); // Prevent unintended actions

                const textarea = document.createElement("textarea");
                textarea.value = order.orderNumber;
                textarea.style.position = "fixed"; // Avoid scrolling
                textarea.style.opacity = 0; // Make invisible
                document.body.appendChild(textarea);
                textarea.select();

                try {
                  if (document.execCommand("copy")) {
                    showToast("success", "Copied to clipboard"); // ✅ Show success only if copy works
                  } else {
                    showToast("error", "Failed to copy tracking numbers.");
                  }
                } catch (err) {
                  showToast("error", "Failed to copy tracking numbers.");
                }

                document.body.removeChild(textarea); // Clean up the textarea
              }}
            />
          </div>
        </>
      ),
    },
    {
      text: (
        <div>
          Placed
          <br /> Date & Time
        </div>
      ),
      dataField: "placedDate",
      sort: true,
      editable: false,

      formatter: (cellContent, order) => {
        if (!order.placedDate) return "";

        const placedDate = new Date(order.placedDate);
        const sendDate = order.sendDate ? new Date(order.sendDate) : null;

        // Сравниваем только дату, без часов и минут
        const sameDay = !sendDate || (placedDate.getFullYear() === sendDate.getFullYear() && placedDate.getMonth() === sendDate.getMonth() && placedDate.getDate() === sendDate.getDate());

        const dateFormatted = placedDate.toLocaleDateString("en-GB");

        const timeFormatted = placedDate.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        });

        const isDifferentDate = sendDate && !sameDay;

        return (
          <div
            className="text-break text-center"
            title={isDifferentDate ? "⚠ Placed Date and Send Date are different" : "Placed Date and Send Date match"}
            style={{
              color: isDifferentDate ? "#d9534f" : undefined,
              fontWeight: isDifferentDate ? "bold" : undefined,
            }}
          >
            {dateFormatted}{" "}
            <span
              className={isDifferentDate ? "" : "text-muted"}
              style={{
                fontSize: "13px",
                color: isDifferentDate ? "#d9534f" : undefined,
                fontWeight: isDifferentDate ? "bold" : undefined,
              }}
            >
              {timeFormatted}
            </span>
            {isDifferentDate && (
              <AlertTriangle
                size={20}
                color="#d9534f"
                strokeWidth={2}
                style={{
                  marginLeft: "6px",
                  marginBottom: "4px",
                }}
              />
            )}
          </div>
        );
      },

      style: {
        width: "15ch",
        maxWidth: "15ch",
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },
    {
      text: "Send Date",
      dataField: "sendDate",
      sort: true,
      csvExport: true,
      hidden: false,
      align: "center",
      headerAlign: "center",

      csvFormatter: (cell, row) => (row.sendDate != null ? new Date(row.sendDate).toLocaleDateString("en-GB") : ""),

      formatter: (cell, order) => {
        if (!cell) {
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                lineHeight: "1.2",
              }}
              title='Put "Send date" or ONLY PACK to place the order'
            >
              <span
                style={{
                  color: "red",
                  fontWeight: "bold",
                  fontSize: "12px",
                  textAlign: "center",
                  marginBottom: "4px",
                }}
              >
                PUT "SEND DATE" OR ONLY PACK
                <br />
                TO PLACE THE ORDER
              </span>
            </div>
          );
        }

        const sendDate = new Date(cell);
        const placedDate = order.placedDate ? new Date(order.placedDate) : null;

        // Сравниваем только даты, без часов и минут
        const sameDay = !placedDate || (placedDate.getFullYear() === sendDate.getFullYear() && placedDate.getMonth() === sendDate.getMonth() && placedDate.getDate() === sendDate.getDate());

        const isDifferentDate = placedDate && !sameDay;

        const formattedDate = sendDate.toLocaleDateString("en-GB");

        return (
          <div
            className="text-center"
            title={isDifferentDate ? "⚠ Placed Date and Send Date are different" : "Placed Date and Send Date match"}
            style={{
              color: isDifferentDate ? "#d9534f" : undefined,
              fontWeight: isDifferentDate ? "bold" : undefined,
            }}
          >
            {formattedDate}

            {isDifferentDate && (
              <AlertTriangle
                size={20}
                color="#d9534f"
                strokeWidth={2}
                style={{
                  marginLeft: "6px",
                  marginBottom: "4px",
                }}
              />
            )}
          </div>
        );
      },

      editor: {
        type: Type.DATE,
        defaultValue: new Date().toISOString().split("T")[0],
      },
    },
    {
      text: (
        <div>
          Box
          <br /> Qty
        </div>
      ),
      dataField: "boxQty",
      sort: true,
      editable: true,
      formatter: (cellContent, order) => (
        <h5 className="font-size-16 mb-1 text-center" style={{ width: "50px" }}>
          {order.boxQty} кор.
        </h5>
      ),
    },

    {
      text: "Box Size",
      dataField: "boxSize",
      sort: true,
      editable: true,
      formatter: (cellContent, order) => {
        // const boxes = (tableData.boxSize || '').split(/[.]+/);
        return (
          <h5 className="text-break text-center">
            {/* {boxes.map((boxes, index) => (
              <div key={index} style={{ marginBottom: '10px' }}>
                {boxes}
              </div>
            ))} */}
            {order.boxSize}
          </h5>
        );
      },
      style: {
        width: "15ch",
        maxWidth: "15ch",
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },
    // {
    //   text: "Send Date",
    //   dataField: "sendDate",
    //   sort: true,
    //   editable: true,
    //   formatter: (cellContent, order) => (
    //     <h5 className="font-size-16 mb-1">
    //       {new Date(order.sendDate).toLocaleDateString("en-GB")}
    //     </h5>
    //   ),
    // },
    {
      text: "Status",
      dataField: "status",
      sort: true,
      editable: false,

      formatter: (cellContent, order) => {
        const meta = getStatusMeta(order.status);

        const hasRealizationNo = Boolean(String(order.realizationNo || "").trim());

        const hasBoxQty = Number(order.boxQty) > 0;

        const statusesRequireBoxQty = ["packaged", "request_slip_docs", "wait_pickup", "sent", "only_pack_packaged", "only_pack_allow_send"];

        return (
          <div>
            <div
              className={`d-flex align-items-center justify-content-between rounded-pill px-3 py-2 fw-bold text-white ${meta?.colorClass || "bg-secondary"}`}
              style={{
                width: "290px",
                gap: "8px",
                position: "relative",
                fontSize: "16px",
                textAlign: "center",
                marginTop: "12px",
                marginBottom: "12px",
              }}
            >
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  gap: "4px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                }}
              >
                {!order.status && <BadgePlus size={16} color="orange" />}

                {meta?.icon}

                <span>{meta?.label || "Set Status"}</span>
              </div>

              <ChevronDown size={16} className="ms-2" />

              <select
                value={order.status || ""}
                onChange={(e) => {
                  const newStatus = e.target.value;

                  // Статус требует Box Qty
                  if (statusesRequireBoxQty.includes(newStatus) && !hasBoxQty) {
                    return;
                  }

                  // Sent дополнительно требует Realization No
                  if (newStatus === "sent" && !hasRealizationNo) {
                    return;
                  }

                  handleSaveCell(order.status, newStatus, order, {
                    dataField: "status",
                  });
                }}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  opacity: 0,
                  cursor: "pointer",
                }}
              >
                <option value="">Set Status</option>

                {STATUS_OPTIONS.filter((option) => {
                  const isAdmin = profileData.accType === "Admin";

                  const isSales = profileData.department?.depName === "Sales Department";

                  if (isAdmin) return true;

                  if (isSales) {
                    return ["stopped", "only_pack", "only_pack_allow_send"].includes(option.key);
                  }

                  return true;
                }).map((option) => {
                  const requiresBoxQty = statusesRequireBoxQty.includes(option.key);

                  const isBoxQtyDisabled = requiresBoxQty && !hasBoxQty;

                  const isSentDisabled = option.key === "sent" && !hasRealizationNo;

                  const isDisabled = isBoxQtyDisabled || isSentDisabled;

                  let label = option.label;

                  if (isBoxQtyDisabled) {
                    label = `⚠ ${option.label} - Box Qty required`;
                  } else if (isSentDisabled) {
                    label = "⚠ Sent - Realization No required";
                  }

                  return (
                    <option
                      key={option.key}
                      value={option.key}
                      disabled={isDisabled}
                      className={isDisabled ? "text-danger fw-bold" : ""}
                      style={
                        isDisabled
                          ? {
                              color: "red",
                              fontWeight: 700,
                            }
                          : undefined
                      }
                    >
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        );
      },

      style: { width: "290px" },
    },
    {
      text: "Product Type WH",
      dataField: "productTypeWh",
      sort: true,
      editable: false,

      formatter: (cellContent, order) => {
        const PRODUCT_TYPE_WH_OPTIONS = [
          {
            key: "products_12",
            label: "Products - #12",
            icon: <ShoppingCart size={18} className="me-2 mb-1" />,
            colorClass: "bg-warning",
          },
          {
            key: "toxins_13",
            label: "Toxins - #13",
            icon: <Snowflake size={18} className="me-2 mb-1" />,
            colorClass: "bg-info bg-gradient",
          },
        ];

        const currentOption = PRODUCT_TYPE_WH_OPTIONS.find((option) => option.key === order.productTypeWh);

        const hasProductTypeWh = Boolean(String(order.productTypeWh || "").trim());

        return (
          <div>
            <div
              className={`d-flex align-items-center justify-content-between rounded-pill px-3 py-2 fw-bold text-white ${currentOption?.colorClass || "bg-secondary"}`}
              style={{
                width: "210px",
                gap: "8px",
                position: "relative",
                fontSize: "16px",
                textAlign: "center",
                marginTop: "12px",
                marginBottom: hasProductTypeWh ? "12px" : "4px",
              }}
            >
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  gap: "4px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                }}
              >
                {!hasProductTypeWh && <BadgePlus size={16} color="orange" />}

                {currentOption?.icon}

                <span>{currentOption?.label || "Select Product Type"}</span>
              </div>

              <ChevronDown size={16} className="ms-2" />

              <select
                value={order.productTypeWh || ""}
                onChange={(e) => {
                  const newProductTypeWh = e.target.value;

                  handleSaveCell(order.productTypeWh, newProductTypeWh, order, {
                    dataField: "productTypeWh",
                  });

                  // Если Product Type изменился — очищаем старый Send Type
                  if (order.productTypeWh !== newProductTypeWh && order.sendType) {
                    handleSaveCell(order.sendType, "", order, {
                      dataField: "sendType",
                    });
                  }
                }}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  opacity: 0,
                  cursor: "pointer",
                }}
              >
                <option value="">Select Product Type</option>

                {PRODUCT_TYPE_WH_OPTIONS.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {!hasProductTypeWh && (
              <div
                className="fw-bold text-danger text-center"
                style={{
                  width: "200px",
                  fontSize: "13px",
                  marginBottom: "12px",
                }}
              >
                ⚠ Product Type WH is required
              </div>
            )}
          </div>
        );
      },

      style: { width: "200px" },
    },
    {
      text: "Address",
      dataField: "address",
      sort: true,
      editable: true,
      formatter: (cellContent, order) => (
        <div className="text-break name-width">
          <h5>{order.address}</h5>
        </div>
      ),
    },
    {
      text: "Send Type",
      dataField: "sendType",
      sort: true,
      editable: false,

      formatter: (cellContent, order) => {
        const isStatusEmpty = !order.status || order.status === "Set Status";

        const isToxin = order.productTypeWh === "toxins_13";

        // OPTIONS FOR NORMAL PRODUCTS
        const PRODUCT_SEND_OPTIONS = [
          {
            value: "cheapSlow",
            label: "UPS Standard",
            icon: <Snail size={20} color="green" className="flex-shrink-0" />,
          },
          {
            value: "worldWideSaver",
            label: "UPS Worldwide Saver",
            icon: <Turtle size={20} color="#c99500" className="flex-shrink-0" />,
          },
          {
            value: "express",
            label: "UPS Express",
            icon: <Rabbit size={20} color="orange" className="flex-shrink-0" />,
          },
          {
            value: "worldWideExpress",
            label: "UPS Worldwide Express Plus",
            icon: <Rocket size={20} color="red" className="flex-shrink-0" />,
          },
          {
            value: "client_pickup",
            label: "Pickup by Client",
            icon: <UserPen size={20} color="blue" className="flex-shrink-0" />,
          },
          {
            value: "client_slip",
            label: "Client provide Slip",
            icon: <ReceiptText size={20} color="blue" className="flex-shrink-0" />,
          },
        ];

        // OPTIONS FOR TOXINS
        const TOXIN_SEND_OPTIONS = [
          {
            value: "toxinPriority",
            label: "Priority",
            icon: <Rabbit size={20} color="orange" className="flex-shrink-0" />,
          },
          {
            value: "toxinPriorityExpress",
            label: "Priority Express",
            icon: <Rocket size={20} color="red" className="flex-shrink-0" />,
          },
        ];

        const sendOptions = isToxin ? TOXIN_SEND_OPTIONS : PRODUCT_SEND_OPTIONS;

        const currentSendOption = sendOptions.find((option) => option.value === order.sendType);

        return (
          <div>
            {!order.sendType && isStatusEmpty && (
              <div
                style={{
                  color: "red",
                  fontWeight: "bold",
                  fontSize: "12px",
                  textAlign: "center",
                  marginBottom: "4px",
                }}
              >
                SELECT TYPE OF SEND <br />
                TO PLACE THE ORDER
              </div>
            )}

            <div className="form-group d-flex align-items-center mt-2 mb-2">
              {currentSendOption?.icon}

              {!order.sendType && <BadgePlus size={20} color="orange" className="flex-shrink-0" />}

              <select
                className="form-control status-selection"
                value={order.sendType || ""}
                name="sendType"
                onChange={(e) =>
                  handleSaveCell(order.sendType, e.target.value, order, {
                    dataField: "sendType",
                  })
                }
              >
                <option value="">{isToxin ? "Select Toxin Send Type" : "Select Type of Send"}</option>

                {sendOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      },

      style: { width: "200px" },
    },
    {
      text: (
        <div>
          Realization
          <br /> No
        </div>
      ),
      dataField: "realizationNo",
      sort: true,
      editable: true,
      formatter: (cellContent, order) => {
        return <h5 className="text-break text-center">{order.realizationNo}</h5>;
      },
      style: {
        width: "15ch",
        maxWidth: "15ch",
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },
    {
      text: "Tracking",
      dataField: "trackingNo",
      sort: true,
      editable: true,
      formatter: (cellContent, order) => <TrackingColumnPrTest tableData={order} />,
    },
    {
      text: (
        <div>
          Delivery
          <br /> Fee
        </div>
      ),
      dataField: "deliveryFee",
      sort: true,
      editable: true,

      editor: {
        type: Type.TEXT,
      },

      formatter: (cellContent, order) => (
        <div className="text-break text-center">
          <h5>{order.deliveryFee ?? ""}</h5>
        </div>
      ),
    },
    {
      text: "Currency",
      dataField: "currency",
      sort: true,
      editable: false,

      formatter: (cellContent, order) => (
        <div className="form-group d-flex align-items-center mt-2 mb-2">
          <select
            className="form-control status-selection"
            value={order.currency || ""}
            name="currency"
            onChange={(e) =>
              handleSaveCell(order.currency, e.target.value, order, {
                dataField: "currency",
              })
            }
          >
            <option value="">Select Currency</option>
            <option value="PNL">PNL</option>
            <option value="EUR">EUR</option>
          </select>
        </div>
      ),

      style: { width: "150px" },
    },
    {
      text: "Comments",
      dataField: "comments",
      sort: true,
      editable: true,
      formatter: (cellContent, order) => (
        <div className="text-break name-width">
          <h5>{order.comments}</h5>
        </div>
      ),
    },
    {
      text: "Manager",
      dataField: "manager",
      sort: true,
      formatter: (cellContent, order) => (
        <div>
          <h5 className="font-size-16 mb-1">
            {order.manager ? (
              <span>
                <div>
                  {!order.manager.profilePic ? (
                    <div className="avatar-sm m-2 d-inline-block">
                      <span className="avatar-title rounded-circle">{order.manager?.fname?.charAt(0)}</span>
                    </div>
                  ) : (
                    <img className="rounded-circle avatar-sm m-2" src={order.manager.profilePic} alt="profile pic" />
                  )}
                  <span>
                    {order.manager.fname} {order.manager.sname}
                  </span>
                  <p className="text-muted mb-0">{order.manager.workEmail}</p>
                </div>
              </span>
            ) : null}
          </h5>
          {/* <h5 className='font-size-16 mb-1'>
            {transfer.manager ? (
              <>
                <span>
                  {transfer.manager.fname}
                  &nbsp;
                  {transfer.manager.sname}
                </span>
                <p className='text-muted mb-0'>{transfer.manager.workEmail}</p>
              </>
            ) : null}
          </h5> */}
        </div>
      ),
    },

    {
      dataField: "menu",
      isDummyField: true,
      editable: false,
      text: `${JSON.parse(localStorage.getItem("authUser")).result.accType === "Admin" ? "Actions" : ""}`,
      formatter: (cellContent, order) => (
        <div className="d-flex gap-3">
          <>
            <Link className="text-success" to="#">
              <i className="mdi mdi-pencil font-size-18" id="edittooltip" onClick={() => handleOrderClickEdit(order)}></i>
            </Link>
            <ScrollText
              size={20}
              color="#0d6efd"
              style={{ cursor: "pointer" }}
              title="View Logs"
              onClick={() => {
                setLogModalOrderId(order._id);
                setShowLogModal(true);
              }}
            />
            <Link className="text-danger" to="#">
              <i className="mdi mdi-delete font-size-18" id="deletetooltip" onClick={() => handleOrderClickDelete(order)}></i>
            </Link>
          </>
        </div>
      ),
    },
  ];
  const defaultSorted = [
    {
      dataField: "placedDate", // if dataField is not match to any column you defined, it will be ignored.
      order: "asc", // desc or asc
    },
  ];

  const selectRow = {
    mode: "checkbox",
    selected: selectedRows,
    onSelect: (row, isSelect) => {
      if (isSelect && row.quantityLeft !== 0) {
        setSelectedRows((prev) => [...prev, row._id]);
      } else {
        setSelectedRows((prev) => prev.filter((id) => id !== row._id));
      }
    },
    onSelectAll: (isSelect, rows) => {
      if (isSelect) {
        const validIds = rows.filter((r) => r.quantityLeft !== 0).map((r) => r._id);
        setSelectedRows(validIds);
      } else {
        setSelectedRows([]);
      }
    },
  };

  //   const depsOptions = departments.sort((a, b) => a.depName.localeCompare(b.depName)).map(({ _id, depName }) => ({
  //     key: _id,
  //     label: depName,
  //     icon: '',
  //     colorClass: 'bg-primary'
  //   }));
  //   const accTypeOptions = [...new Set(users.map(u => u.accType))].map(type => ({ key: type, label: type, icon: '', colorClass: 'bg-primary' }));
  //   const groupAccessOptions = (accesses) => {
  //     const groups = {};

  //     Object.keys(accesses).forEach((key) => {
  //       if (key === '_id' || key === '__v') return;

  //       // Get prefix (first lowercase part before a capital)
  //       const prefixMatch = key.match(/^[a-z]+/);
  //       const prefix = prefixMatch ? prefixMatch[0] : 'Other';

  //       if (!groups[prefix]) groups[prefix] = [];

  //       groups[prefix].push({
  //         value: key,
  //         text: key
  //           .split(/(?=[A-Z])/)
  //           .join(' ')
  //           .replace(/^./, (c) => c.toUpperCase()),
  //       });
  //     });

  //     return groups;
  //   };
  //   const accessGroups = groupAccessOptions(accesses);
  // const initialSelected = useMemo(() => ({
  //   accountTypes: accTypeOptions
  //     .filter(option => option.key !== 'laid_off')
  //     .map(option => option.key)
  // }), [accTypeOptions]);
  //   const filterFormValues = { accountTypes: ['Employee', 'Outsider', 'Admin'] }
  // const filterFormValues = { statuses: ['eng'] };

  return (
    <React.Fragment>
      {/* <PageLoader progress={progress} step={step} visible={visible} /> */}
      <div className="page-content">
        <MetaTags>
          <title>Priority Poland</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs title="Admin" breadcrumbItem="Priority Poland" />
          <Row className="card-section">
            <Col lg="12">
              <div className="pcss3t logtb pcss3t-effect-scale pcss3t-theme-1">
                <ul>
                  <AnimatePresence key="1" exitBeforeEnter>
                    <motion.li
                      className={`tab-content tab-content-1 typography`}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.3 }}
                      style={{ display: "block" }}
                    >
                      <div id="card-section" ref={cardRef}>
                        <Row className="card-section">
                          <Col lg="12">
                            <Card>
                              <CardBody className="cardBodySticky">
                                <ToolkitProvider keyField="_id" columns={columns} data={filteredPriorityPL} search>
                                  {(props) => (
                                    <React.Fragment>
                                      <MainContent
                                        page={{
                                          pg: "priorityPL",
                                          props: props,
                                          cardHeight,
                                          showSearch: true,
                                          showCSV: true,
                                          showInTblFltr: true,
                                          showDbFilter: false,
                                          showBtn: true,
                                          columns: props.baseProps.columns,
                                          data: props.baseProps.data,
                                          defaultSorted,
                                          selectRow,
                                          cellEdit: cellEditFactory({
                                            mode: "click",
                                            blurToSave: true,
                                            afterSaveCell: (oldVal, newVal, row, column, rowId) => handleSaveCell(oldVal, newVal, row, column, rowId),
                                          }),
                                        }}
                                        infoPopoverOptions={[
                                          {
                                            isAdmin: profileData.accType === "Admin",
                                            boardKey: "board-priority-poland-sales",
                                            notify: true,
                                            adminOnly: false,
                                            userId: profileData._id,
                                          },
                                          {
                                            isAdmin: profileData.accType === "Admin",
                                            boardKey: "board-priority-poland",
                                            boardImageType: "admin",
                                            adminOnly: true,
                                            userId: profileData._id,
                                          },
                                        ]}
                                        // filter options
                                        filterOptions={{
                                          status: STATUS_OPTIONS,
                                        }}
                                        notificationSettings={{
                                          showNotification: true,
                                          keyword: "priority_pl",
                                          module: ["Bot Helper Notification", "Bot Priority Notification"],
                                        }}
                                        // filterDbOptions={{filterFormValues} }
                                        setNoDbFilters={setNoDbFilters}
                                        // options for adding/updating modal and button
                                        btnCrtModalOptions={{
                                          btnLabelCrt: "Add New Order",
                                          modalSize: "xl",
                                          modalTitle: isEdit ? "Edit Order" : "Add Order",
                                          modalBtnLabel: isEdit ? "Edit Order" : "Add Order",
                                          modalAdd: modalNewOrder,
                                          toggleAddModal: toggleNewOrderModal,
                                          handleNewClick: handleNewOrderClick,
                                          modalContent: modalNewOrderContent,
                                          handleSubmit: handleNewOrderSubmit,
                                        }}
                                        // table data opotions
                                        tableOptions={{
                                          includeSelect: true,
                                          manualColumnWidthsIndex: [7, 8, 9, 10, 11, 12],
                                          manualColumnWidths: [390, 220, 220, 150, 300, 270],
                                        }}
                                        // table actions
                                        // tableActions={{
                                        //   globalActionOptions: STATUS_OPTIONS_SELECT,
                                        //   globalActionBtnLbl: "APPLY",
                                        //   handleGlobalActionSubmit,
                                        // }}
                                      />

                                      <SweetAlert
                                        show={showDeleteAlert}
                                        danger
                                        showCancel
                                        confirmBtnText="Yes, delete it!"
                                        confirmBtnBsStyle="danger"
                                        title="Are you sure?"
                                        onConfirm={handleDeleteOrder}
                                        onCancel={() => setShowDeleteAlert(false)}
                                        focusCancelBtn
                                      >
                                        <p className="fw-bold">{order.orderNumber}</p>
                                        will be deleted from the system!
                                      </SweetAlert>

                                      <Modal isOpen={showLogModal} toggle={() => setShowLogModal(false)} size="xl" className="log-modal modal-lg-custom">
                                        <ModalHeader toggle={() => setShowLogModal(false)}>
                                          <h3>Order Logs</h3>
                                        </ModalHeader>
                                        <ModalBody>
                                          {logsLoading ? (
                                            <p>Loading logs...</p>
                                          ) : logs.length === 0 ? (
                                            <p>No logs found for this order.</p>
                                          ) : (
                                            <div className="log-timeline">
                                              {logs.map((log) => (
                                                <div key={log._id} className="timeline-item">
                                                  <div className="timeline-dot" />
                                                  <div className="timeline-content">
                                                    <small className="text-muted">
                                                      {new Date(log.timestamp).toLocaleString("en-GB", {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                        day: "2-digit",
                                                        month: "2-digit",
                                                        year: "numeric",
                                                      })}
                                                      {log.createdBy?.fname && ` • ${log.createdBy.fname} ${log.createdBy.sname}`}
                                                    </small>
                                                    <strong>{log.action}</strong>
                                                    <p className="log-details">{log.details}</p>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </ModalBody>
                                      </Modal>
                                    </React.Fragment>
                                  )}
                                </ToolkitProvider>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </div>
                    </motion.li>
                  </AnimatePresence>
                </ul>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}
